/**
 * Loading a map (Stage 4, 2026-10-20): the heaviest thing the app loads, so it shows progress through its real steps -
 * Map (the edition's data), Ground (the 3D code, then the terrain and scene being built), Scene (the first frame drawn).
 * MapsPage passes `step` 0-3 as they happen; 3 = done: the dot lands and the loader fades off the map.
 * Variants: MAP_LOADERS.
 */
import { useEffect, useState } from 'react';
import PetalMark from './PetalMark';

// Sam, 2026-10-20: the petals must read as "working" from the first moment (the bar can sit still while the scene
// builds), so every option starts in the spinning stage - no build-then-spin. Each sits over the bar and the step names.
export const MAP_LOADERS = {
  now: {
    label: 'Spinner at once',
    note: 'Faint petals from the first frame, the head already spinning.',
    petal: 'spinnerNow',
  },
  quick: {
    label: 'Spinner, quick start',
    note: 'The faint petals sweep in in a quarter second, then spin.',
    petal: 'spinnerQuick',
  },
  comet: {
    label: 'Three going round',
    note: 'Three petals at a time travel round; done, the circle fills and the dot lands.',
    petal: 'comet3',
  },
};
const STEPS = ['Map', 'Ground', 'Scene'];

/** Progress that never stalls: inside a step it creeps toward that step's end, so a long build still moves. */
function useCreep(step) {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (step >= 3) {
      setP(1);
      return undefined;
    }
    const lo = step / 3,
      hi = (step + 1) / 3;
    setP((cur) => Math.max(cur, lo));
    const t = setInterval(() => setP((cur) => Math.min(hi - 0.01, cur + (hi - cur) * 0.08)), 120);
    return () => clearInterval(t);
  }, [step]);
  return p;
}

export default function MapLoader({ variant = 'bar', step = 0, name = null, onDone }) {
  const progress = useCreep(step);
  const [out, setOut] = useState(false);
  const finished = () => {
    setOut(true);
    setTimeout(() => onDone?.(), 240);
  };
  const v = MAP_LOADERS[variant] ? variant : 'comet';
  const shown = Math.min(step, 2);
  return (
    <div
      className={`map-loader${out ? ' map-loader--out' : ''}`}
      role="status"
      aria-label={`Loading ${name ?? 'the map'}: ${STEPS[shown]}`}
    >
      <PetalMark
        variant={MAP_LOADERS[v].petal}
        size={84}
        ready={step >= 3}
        glow={false}
        onFinished={finished}
      />
      {name && <p className="map-loader__name">{name}</p>}
      <div className="map-loader__track">
        <i style={{ transform: `scaleX(${progress})` }} />
      </div>
    </div>
  );
}
